import {
  StatisticsAnimalType,
  StatisticsFigures,
  StatisticsResponse
} from './statistics.model';

/** Eine Spalte der Aufenthaltsort-Gruppe, Reihenfolge und Beschriftung kommen aus der API. */
export interface StatisticsLocationColumn {
  location: string;
  label: string;
}

/** Eine Zeile der Tiergruppen-Tabelle. Die Gesamtzeile ist die letzte Zeile. */
export interface StatisticsCategoryRow {
  /** Tiergruppen-Schluessel, fuer die Gesamtzeile `total`. */
  key: string;
  label: string;
  isTotal: boolean;
  /** Weder Bestand noch Bewegung im Zeitraum. */
  isEmpty: boolean;
  /** Zusatz neben dem Namen, z. B. "5 Tierarten". */
  hint: string;
  figures: StatisticsFigures;
  /** Zahlen des Vorjahres, sofern geladen - sonst null. */
  previous: StatisticsFigures | null;
  /** Bestand je Aufenthaltsort, deckungsgleich mit locationColumns. */
  locationCounts: number[];
  /** Tiere mit Abgang ohne Bestand in dieser Tiergruppe. */
  dataQualityCount: number;
  /** Tierarten der Tiergruppe; bei der Gesamtzeile die acht Tiergruppen. */
  breakdown: StatisticsBreakdownRow[];
  breakdownTitle: string;
  breakdownColumn: string;
  breakdownNote: string;
}

/** Zeile der aufgeklappten Tierart- bzw. Tiergruppen-Aufschluesselung. */
export interface StatisticsBreakdownRow {
  label: string;
  figures: StatisticsFigures;
}

/** Ein Eintrag der Herkunfts- oder Verbleibsliste mit Balkenanteil. */
export interface StatisticsDistributionItem {
  label: string;
  count: number;
  /** Anteil am groessten Wert der Liste, 0-100. */
  percent: number;
}

const EMPTY_FIGURES_NOTE = 'keine Bewegung';

/** Zahlen im deutschen Format, ohne Nachkommastellen. */
export function formatCount(value: number): string {
  return value.toLocaleString('de-DE');
}

/** Veraenderung mit Vorzeichen, "±0" bei Gleichstand. */
export function formatDelta(value: number): string {
  if (value > 0) {
    return `+${formatCount(value)}`;
  }
  if (value < 0) {
    return `−${formatCount(Math.abs(value))}`;
  }
  return '±0';
}

/** Datum als TT.MM.JJJJ, leer bei fehlendem Wert. */
export function formatDate(isoDate: string | null | undefined): string {
  if (!isoDate) {
    return '';
  }
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'});
}

/** Beschriftung des Endbestands - im laufenden Jahr der heutige Stichtag. */
export function stockEndLabel(response: StatisticsResponse): string {
  const end = formatDate(response.periodEnd);
  return end ? `Stand ${end.slice(0, 6)}` : 'Endbestand';
}

export function stockStartLabel(response: StatisticsResponse): string {
  const start = formatDate(response.periodStart);
  return start ? `Stand ${start.slice(0, 6)}` : 'Anfangsbestand';
}

export function locationColumns(response: StatisticsResponse): StatisticsLocationColumn[] {
  return response.total.stockBreakdown.byLocation.map(entry => ({
    location: entry.location,
    label: entry.label
  }));
}

/** Bestand je Aufenthaltsort in der Reihenfolge der Spalten. */
export function locationCounts(figures: StatisticsFigures, columns: StatisticsLocationColumn[]): number[] {
  return columns.map(column => {
    const match = figures.stockBreakdown.byLocation.find(entry => entry.location === column.location);
    return match ? match.count : 0;
  });
}

/** true, wenn im Zeitraum weder Bestand noch Bewegung vorlag. */
export function isEmptyFigures(figures: StatisticsFigures): boolean {
  return figures.stockAtPeriodStart === 0
    && figures.admissions === 0
    && figures.departures === 0;
}

/** Herkunfts- oder Verbleibsliste mit Balkenanteilen relativ zum groessten Wert. */
export function toDistribution(
  entries: { label: string; count: number }[]
): StatisticsDistributionItem[] {
  const max = Math.max(1, ...entries.map(entry => entry.count));
  return entries.map(entry => ({
    label: entry.label,
    count: entry.count,
    percent: (entry.count / max) * 100
  }));
}

/**
 * Baut die Zeilen der Tiergruppen-Tabelle inklusive Gesamtzeile.
 * `previous` ist die Statistik des Vorjahres, sofern bereits geladen.
 */
export function buildCategoryRows(
  response: StatisticsResponse,
  previous: StatisticsResponse | null
): StatisticsCategoryRow[] {
  const columns = locationColumns(response);
  const dataQualityByCategory = new Map(
    response.dataQuality.byCategory.map(entry => [entry.category, entry.animalsWithDepartureWhileNotInStock])
  );

  const rows: StatisticsCategoryRow[] = response.categories.map(category => {
    const empty = isEmptyFigures(category.figures);
    return {
      key: category.category,
      label: category.categoryLabel,
      isTotal: false,
      isEmpty: empty,
      hint: categoryHint(category.animalTypes, empty),
      figures: category.figures,
      previous: previous?.categories.find(entry => entry.category === category.category)?.figures ?? null,
      locationCounts: locationCounts(category.figures, columns),
      dataQualityCount: dataQualityByCategory.get(category.category) ?? 0,
      breakdown: category.animalTypes.map(animalType => ({
        label: animalType.animalTypeLabel,
        figures: animalType.figures
      })),
      breakdownTitle: 'Tierarten',
      breakdownColumn: 'Tierart',
      breakdownNote: 'Nur Tierarten, die tatsächlich vorkommen. Jede Zeile geht für sich auf.'
    };
  });

  rows.push({
    key: 'total',
    label: 'Gesamt',
    isTotal: true,
    isEmpty: isEmptyFigures(response.total),
    hint: '',
    figures: response.total,
    previous: previous?.total ?? null,
    locationCounts: locationCounts(response.total, columns),
    dataQualityCount: response.dataQuality.animalsWithDepartureWhileNotInStock,
    breakdown: response.categories.map(category => ({
      label: category.categoryLabel,
      figures: category.figures
    })),
    breakdownTitle: 'Tiergruppen',
    breakdownColumn: 'Tiergruppe',
    breakdownNote: 'Alle acht Tiergruppen, auch die ohne Bewegung.'
  });

  return rows;
}

function categoryHint(animalTypes: StatisticsAnimalType[], empty: boolean): string {
  if (empty) {
    return EMPTY_FIGURES_NOTE;
  }
  if (animalTypes.length > 1) {
    return `${animalTypes.length} Tierarten`;
  }
  return '';
}
